// ============================================
// ENUMS - Backend Data Types
// ============================================

export enum MessageType {
  TEXT = 'TEXT',
  AUDIO = 'AUDIO',
  FILE = 'FILE',
  SYSTEM = 'SYSTEM',
}

export enum MessageStatus {
  DELIVERED = 'DELIVERED',
  READ = 'READ',
  DELETED = 'DELETED',
}

enum TopicType {
  GROUP = 'GROUP',
  DIRECT = 'DIRECT',
  CALL_ROOM = 'CALL_ROOM',
  TICKET = 'TICKET',
}

enum TopicStatus {
  ACTIVE = 'ACTIVE',
  DELETED = 'DELETED',
}

enum TopicRole {
  ADMIN = 'ADMIN',
  DEFAULT = 'DEFAULT',
  VIEWER = 'VIEWER',
}

enum WorkspaceStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  DELETED = 'DELETED',
}

export enum WorkspaceRole {
  ADMIN = 'ADMIN',
  }

export enum PresenceStatus {
  ONLINE = 'ONLINE',
  }

// ============================================
// BACKEND API TYPES - From CRM Backend
// ============================================

export interface Message {
  id: string;
  topicId: string;
  senderId: string;
  content: string;
  type: MessageType;
  status: MessageStatus;
  sentAt: string;
  updatedAt: string;
  deletedAt?: string;
  pinned?: boolean;
  repliedTo?: {
    messageId: string;
    senderId: string;
    content: string;
    type: string;
  };
  fileIds?: string[];
  reactions?: Record<string, string[]>; // emoji -> user IDs
  ref?: MessageRef;
}

export interface ChatFile {
  id: string;
  name: string;
  ext: string;
  size: number;
  createdAt: string;
  /** Content-Type do arquivo. */
  type: string;
  topicId: string;
  /** `PENDING` até o upload ser confirmado; só arquivos confirmados vão em mensagens. */
  status?: string;
  uploaderId?: string;
}

/** Formulário assinado para enviar o arquivo direto ao storage, sem passar pelo chat. */
export interface ChatFileUploadGrant {
  file: ChatFile;
  url: string;
  method: string;
  fields: Record<string, string>;
}

export interface ChatFileDownload {
  url: string;
  expiresAt: string;
}

export interface Topic {
  id: string;
  workspaceId: string;
  name?: string;
  description?: string;
  type: TopicType;
  members: TopicMember[];
  status: TopicStatus;
  pinned?: boolean;
  lastMessage?: {
    content: string;
    senderId: string;
    sentAt: string;
  };
  /** Por ler segundo o marcador do servidor (por pessoa). Ausente em servidores antigos. */
  unread?: boolean;
}

export type TopicMember = string | {
  userId: string;
  role?: TopicRole | string;
};

export interface Workspace {
  id: string;
  externalId?: string;
  name: string;
  members: WorkspaceMember[];
  status: WorkspaceStatus;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export type WorkspaceMember = string | {
  userId: string;
  role?: WorkspaceRole | string;
};

export interface PagedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export interface UserPresence {
  userId: string;
  status: PresenceStatus;
  lastSeen?: number;
}

export interface TypingNotification {
  userId: string;
  isTyping: boolean;
}

// ============================================
// FRONTEND UI TYPES - For Display
// ============================================

/** Tarefa, registo do diário ou obra de que a mensagem fala (rótulo do momento do envio). */
export interface MessageRef {
  kind: 'TASK' | 'DIARY' | 'PROJECT';
  id: string;
  label: string;
  projectId?: string;
}

export interface ChatMessage {
  ref?: MessageRef;
  id: string;
  text?: string;
  senderId: string;
  senderName?: string;
  senderAvatar?: string;
  timestamp: string;
  sentAt?: string;
  status: 'sent' | 'delivered' | 'read' | 'error';
  type?: 'text' | 'image' | 'audio' | 'file' | 'system';
  pinned?: boolean;
  repliedTo?: {
    messageId: string;
    senderId: string;
    content: string;
    type: string;
  };
  fileIds?: string[];
  files?: ChatFile[];
  reactions?: Record<string, string[]>;
}

export interface ChatContact {
  id: string;
  name: string;
  avatar?: string;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount?: number;
  status: 'online' | 'offline';
  role: 'Cliente' | 'Fornecedor' | 'Colaborador' | 'Administrador';
  projectName?: string;
  /** Workspace (obra) da conversa; vazio nas conversas avulsas. */
  workspaceId?: string;
  /** Canal padrão da obra (GENERAL, PROBLEMS, ...); vazio nos canais criados pela equipa. */
  channelKey?: string;
  /** Id da obra no sistema (externalId do workspace), para abrir a obra. */
  projectId?: string;
  topicType?: 'GROUP' | 'DIRECT' | 'CALL_ROOM' | 'TICKET';
  description?: string;
  isTyping?: boolean;
  pinned?: boolean;
  members?: {
    id: string;
    name: string;
    avatar?: string;
    role?: string;
    roleType?: string;
    isAdmin?: boolean;
    status?: 'online' | 'offline';
  }[];
}

export interface WorkProject {
  id: string;
  name: string;
  status: string;
  link: string;
}

export interface ChatDocument {
  id: string;
  name: string;
  type: string;
  date: string;
}

export interface Reminder {
  id: string;
  title: string;
  dueDate: string;
}
